import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocalStorage } from 'usehooks-ts';

import { ModalContext } from '../components/modals/ModalManager';
import SelectTeamModal from '../components/modals/SelectTeamModal';
import { TeamMemberStatuses, TeamRoles } from '../constants/TeamRoles';
import {
    Person,
    Team,
    TeamMemberWithDetails,
    useGetPersonDetailsQuery,
} from '../store/sageHomeApi';
import { selectBearerToken } from '../store/slices/authSlice';
import {
    SELECTED_TEAM_KEY,
    selectCurrentTeamRid,
    setPersonRid,
    setSelectedTeamRid,
} from '../store/slices/teamMemberSlice';
import { useAppDispatch } from '../store/store';

export interface TeamMemberDataResult {
    // Loading states from RTK Query
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    isSuccess: boolean;
    error: unknown;

    // Raw data
    person?: Person;
    teams: Team[];

    // Computed/derived data
    currentRecipientAddress?: string;
    currentTeam?: Team;
    currentTeamRid?: string;
    currentTeamTimezone?: string;

    // Current user with full membership details merged (person + teamMember)
    currentUser?: TeamMemberWithDetails;
    careRecipient?: TeamMemberWithDetails;
    communityCaregivers: TeamMemberWithDetails[];
    licensedCaregivers: TeamMemberWithDetails[];
    invitedCaregivers: TeamMemberWithDetails[];
    teamAdmins: TeamMemberWithDetails[];
    allCaregivers: TeamMemberWithDetails[];

    // Helper data structures
    teamMemberByRidMap: Map<string, TeamMemberWithDetails>;
    teamMemberByPersonRidMap: Map<string, TeamMemberWithDetails>;
    getTeamMemberDetails: (entityRid: string) => TeamMemberWithDetails | undefined;

    // Actions
    setSelectedTeamRid: (teamRid: string) => void;
    refetch: () => void;

    // Onboarding status
    /** Whether the current user has completed onboarding (terms accepted and status is ACTIVE) */
    hasOnboarded: boolean;
}

/**
 * Central hook for fetching and accessing team member data.
 * Uses RTK Query for caching and provides loading/fetching states.
 *
 * This hook:
 * - Fetches team member details using RTK Query
 * - Manages selected team RID (persisted in localStorage and Redux)
 * - Computes derived values (current team, care recipient, caregivers)
 * - Exposes loading/fetching states for UI feedback
 */
export function useTeamMemberData(): TeamMemberDataResult {
    const dispatch = useAppDispatch();
    const { openModal } = useContext(ModalContext);

    const [lastStoredTeamRid, setStoredTeamRid] = useLocalStorage(SELECTED_TEAM_KEY, '');
    const currentTeamRid = useSelector(selectCurrentTeamRid);
    const bearerToken = useSelector(selectBearerToken);

    const {
        data: personDetails,
        isLoading,
        isFetching,
        isError,
        isSuccess,
        error,
        refetch,
    } = useGetPersonDetailsQuery({ authorization: bearerToken }, { skip: !bearerToken });

    const { person, teams = [] } = personDetails ?? {};

    // Store teamMemberRid in Redux for use in API headers
    useEffect(() => {
        if (person?.personRid) {
            dispatch(setPersonRid(person.personRid));
        }
    }, [person?.personRid, dispatch]);

    // Handle team selection on initial load
    useEffect(() => {
        if (teams.length > 0) {
            if (teams.length > 1) {
                // If more than 1 team, check localStorage or open modal
                if (lastStoredTeamRid && teams.some((team) => team.teamRid === lastStoredTeamRid)) {
                    dispatch(setSelectedTeamRid(lastStoredTeamRid));
                } else if (!currentTeamRid) {
                    // Open modal to select team only if no team is selected
                    openModal({ content: SelectTeamModal });
                }
            } else if (teams.length === 1 && !currentTeamRid) {
                // Auto-select single team
                const teamRid = teams[0].teamRid;
                dispatch(setSelectedTeamRid(teamRid));
                setStoredTeamRid(teamRid);
            }
        }
    }, [currentTeamRid, dispatch, lastStoredTeamRid, openModal, setStoredTeamRid, teams]);

    // Compute current team based on selected team RID
    const currentTeam = useMemo(
        () => teams.find((team) => team.teamRid === currentTeamRid),
        [teams, currentTeamRid]
    );

    const currentRecipientAddress = useMemo(() => {
        const { addressLine1, addressLine2, city, state, zipcode } =
            currentTeam?.teamConfigurations?.address || {};

        return [addressLine1, addressLine2, city, state, zipcode].filter(Boolean).join(', ');
    }, [currentTeam?.teamConfigurations?.address]);

    // Merge teamMember with currentTeamMembership to get the full current user
    const currentUser = useMemo(() => {
        if (!person || !currentTeam?.members) return undefined;

        const teamMembership = currentTeam.members.find(
            (member) => member.personRid === person.personRid
        );

        return { ...person, ...teamMembership } as TeamMemberWithDetails;
    }, [currentTeam?.members, person]);

    const {
        careRecipient,
        licensedCaregivers,
        communityCaregivers,
        invitedCaregivers,
        teamAdmins,
    } = useMemo(
        () =>
            (currentTeam?.members || []).reduce(
                (acc, member) => {
                    switch (member.role) {
                        case TeamRoles.CareRecipient:
                            // eslint-disable-next-line no-param-reassign
                            acc.careRecipient = member;
                            break;
                        case TeamRoles.LicensedCaregiver:
                            acc.licensedCaregivers.push(member);
                            break;
                        case TeamRoles.CommunityCaregiver:
                        case TeamRoles.TeamAdmin:
                            acc.communityCaregivers.push(member);
                            break;
                    }

                    // TODO: Remove isTeamAdmin flag usage after we're fully not using `isAdmin`
                    if (member.isTeamAdmin || member.role === TeamRoles.TeamAdmin) {
                        acc.teamAdmins.push(member);
                    }

                    if (member.teamMemberStatus === TeamMemberStatuses.Invited) {
                        acc.invitedCaregivers.push(member);
                    }

                    return acc;
                },
                {
                    careRecipient: undefined,
                    licensedCaregivers: [],
                    communityCaregivers: [],
                    invitedCaregivers: [],
                    teamAdmins: [],
                } as {
                    careRecipient?: TeamMemberWithDetails;
                    licensedCaregivers: TeamMemberWithDetails[];
                    communityCaregivers: TeamMemberWithDetails[];
                    invitedCaregivers: TeamMemberWithDetails[];
                    teamAdmins: TeamMemberWithDetails[];
                }
            ),
        [currentTeam?.members]
    );

    // Check if current user has completed onboarding
    const hasOnboarded = useMemo(() => {
        const { isTermsAccepted, teamMemberStatus } = currentUser ?? {};
        return isTermsAccepted === true && teamMemberStatus === TeamMemberStatuses.Active;
    }, [currentUser]);

    const currentTeamTimezone = useMemo(() => {
        return currentTeam?.teamConfigurations?.ianaTimezone;
    }, [currentTeam]);

    const { teamMemberByRidMap, teamMemberByPersonRidMap } = useMemo(
        () =>
            currentTeam?.members?.reduce(
                (acc, member) => {
                    acc.teamMemberByRidMap.set(member.teamMemberRid, member);
                    acc.teamMemberByPersonRidMap.set(member.personRid, member);
                    return acc;
                },
                {
                    teamMemberByRidMap: new Map<string, TeamMemberWithDetails>(),
                    teamMemberByPersonRidMap: new Map<string, TeamMemberWithDetails>(),
                }
            ) || {
                teamMemberByRidMap: new Map<string, TeamMemberWithDetails>(),
                teamMemberByPersonRidMap: new Map<string, TeamMemberWithDetails>(),
            },
        [currentTeam]
    );

    const getTeamMemberDetails = useCallback(
        (entityRid: string) =>
            teamMemberByRidMap.get(entityRid) ?? teamMemberByPersonRidMap.get(entityRid),
        [teamMemberByRidMap, teamMemberByPersonRidMap]
    );

    // Action to set selected team
    const handleSetSelectedTeamRid = (teamRid: string) => {
        dispatch(setSelectedTeamRid(teamRid));
        setStoredTeamRid(teamRid);
    };

    return {
        // Loading states
        isLoading,
        isFetching,
        isError,
        isSuccess,
        error,

        // Raw data
        person,
        teams,

        // Computed data
        currentRecipientAddress,
        currentTeam,
        currentTeamRid,
        currentTeamTimezone,
        currentUser,
        careRecipient,
        communityCaregivers,
        licensedCaregivers,
        invitedCaregivers,
        teamAdmins,
        allCaregivers: [...licensedCaregivers, ...communityCaregivers],

        // Helper
        teamMemberByRidMap,
        teamMemberByPersonRidMap,
        getTeamMemberDetails,

        // Actions
        setSelectedTeamRid: handleSetSelectedTeamRid,
        refetch,

        // Onboarding status
        hasOnboarded,
    };
}
